import React, { useEffect, useState } from "react";
import moment from "moment";
import { Alert, Button, DatePicker, Select, Space, Tag, Typography } from "antd";
import { axiosInstant, endpoints } from "../../api";
import { showError, showSuccess } from "../../Notifications";
import { IAvailableSlot, getFormattedHours } from "./helper";
import { localToUtc, utcSlotToLocalDisplay, zoneLabel } from "./slotTime";

// Slots from the server are UTC "MM-DD" + hour. Everything shown here is converted
// to the viewer's time zone (named on each panel).
const slotLabel = (date: string, hour: number) => {
  const l = utcSlotToLocalDisplay(date, hour);
  return `${l.when.format("ddd, MMM D")} at ${getFormattedHours(l.hour)}`;
};

interface OverlapSuggestion {
  date: string;
  hour: number;
  freeFor: string[];
  missing: string[];
}

// No slot is shared by everyone selected. The hiring manager's availability comes
// first: a manager with none is asked to add some, and the candidate is asked to
// fit the managers' slots (up to 5, next 14 days). The recruiter sends it in one
// click.
export const OverlapSuggestions: React.FC<{
  jobApplicationId: string;
  userIds: string[];
}> = ({ jobApplicationId, userIds }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [data, setData] = useState<{
    hasOverlap: boolean;
    managersToPrompt: string[];
    suggestions: OverlapSuggestion[];
    names: Record<string, string>;
  }>();
  const key = userIds.slice().sort().join(",");

  useEffect(() => {
    setSent(false);
    if (!jobApplicationId || userIds.length < 2) {
      setData(undefined);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axiosInstant
      .get(endpoints.slotSuggestions.preview(jobApplicationId, userIds))
      .then((res) => !cancelled && setData(res.data))
      .catch(() => !cancelled && setData(undefined))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobApplicationId, key]);

  if (loading || !data || data.hasOverlap) return null;

  const nameOf = (id: string) => data.names[id] || "Participant";
  const askedToFit = Array.from(new Set(data.suggestions.flatMap((s) => s.missing))).map(nameOf);
  const notified = [...data.managersToPrompt.map(nameOf), ...askedToFit];
  const nothingToDo = notified.length === 0;

  const send = async () => {
    setSending(true);
    try {
      await axiosInstant.post(endpoints.slotSuggestions.send, { jobApplicationId, userIds });
      setSent(true);
      showSuccess(`Notified ${notified.join(", ")}`);
    } catch (err) {
      showError(err);
    }
    setSending(false);
  };

  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 16 }}
      message="No overlapping slots"
      description={
        nothingToDo ? (
          <span>
            Nothing to suggest in the next 14 days. Ask the participants to add availability.
          </span>
        ) : (
          <div>
            {data.managersToPrompt.length > 0 && (
              <p style={{ marginTop: 0 }}>
                <strong>{data.managersToPrompt.map(nameOf).join(", ")}</strong>{" "}
                {data.managersToPrompt.length === 1 ? "has" : "have"} no availability in the next 14
                days and will be asked to add some. Hiring manager availability comes first;
                the candidate then fits around it.
              </p>
            )}
            {data.suggestions.length > 0 && (
              <>
                <p style={{ marginTop: 0 }}>
                  Slots {askedToFit.join(", ")} will be asked to open ({zoneLabel()}):
                </p>
                <ul style={{ paddingLeft: 20 }}>
                  {data.suggestions.map((s) => (
                    <li key={`${s.date}-${s.hour}`}>
                      <strong>{slotLabel(s.date, s.hour)}</strong>{" "}
                      - {s.freeFor.map(nameOf).join(", ")} free
                    </li>
                  ))}
                </ul>
              </>
            )}
            <Button type="primary" onClick={send} loading={sending} disabled={sent}>
              {sent ? "Notified" : `Notify ${notified.join(" & ")}`}
            </Button>
          </div>
        )
      }
    />
  );
};

const hourOptions = Array.from({ length: 24 }, (_, h) => ({ value: h, label: getFormattedHours(h) }));

// Recruiter -> candidate: offer any dates and times. The recruiter builds a short
// list (a day plus a from/until range each time), sends it, and the candidate
// adds whichever of those suit them.
export const OfferSlotsPanel: React.FC<{
  jobApplicationId: string;
  candidateId: string;
}> = ({ jobApplicationId, candidateId }) => {
  // Picked in the recruiter's own time zone; sent as UTC.
  const [day, setDay] = useState<string>(); // YYYY-MM-DD
  const [from, setFrom] = useState<number>(10);
  const [until, setUntil] = useState<number>(13);
  const [offered, setOffered] = useState<{ day: string; time: number[] }[]>([]);
  const [sending, setSending] = useState(false);

  if (!jobApplicationId || !candidateId) return null;

  const add = () => {
    if (!day || until <= from) return;
    const hours = Array.from({ length: until - from }, (_, i) => from + i);
    setOffered((cur) => {
      if (!cur.find((d) => d.day === day)) return [...cur, { day, time: hours }].sort((a, b) => a.day.localeCompare(b.day));
      return cur.map((d) =>
        d.day === day ? { ...d, time: Array.from(new Set([...d.time, ...hours])).sort((a, b) => a - b) } : d
      );
    });
  };
  const remove = (d: string, h: number) =>
    setOffered((cur) =>
      cur.map((x) => (x.day === d ? { ...x, time: x.time.filter((t) => t !== h) } : x)).filter((x) => x.time.length)
    );

  const send = async () => {
    const byUtcDay = new Map<string, { date: string; year: number; hours: Set<number> }>();
    offered.forEach(({ day: d, time }) => {
      const [year, month, date] = d.split("-").map(Number);
      time.forEach((h) => {
        const u = localToUtc(`${String(month).padStart(2, "0")}-${String(date).padStart(2, "0")}`, h, year);
        const k = `${u.year}-${u.date}`;
        const day = byUtcDay.get(k) || { date: u.date, year: u.year, hours: new Set<number>() };
        byUtcDay.set(k, { ...day, hours: day.hours.add(u.hour) });
      });
    });
    const slots = Array.from(byUtcDay.values()).map(({ date, year, hours }) => ({ date, year, time: Array.from(hours).sort((a, b) => a - b) }));
    setSending(true);
    try {
      await axiosInstant.post(endpoints.slotSuggestions.offer, { jobApplicationId, candidateId, slots });
      setOffered([]);
      showSuccess("Your available times were sent to the candidate");
    } catch (err) {
      showError(err);
    }
    setSending(false);
  };

  return (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 16 }}
      message="Send your available times to the candidate"
      description={
        <div>
          <p style={{ marginTop: 0 }}>
            Choose a day and a range of hours you are free ({zoneLabel()}). The candidate is
            notified and adds whichever of them suit them to their availability.
          </p>
          <Space wrap style={{ marginBottom: 8 }}>
            <DatePicker
              placeholder="Pick a day"
              disabledDate={(d) => d.isBefore(new Date(), "day")}
              onChange={(d) => setDay(d ? d.format("YYYY-MM-DD") : undefined)}
            />
            <Select style={{ width: 120 }} value={from} options={hourOptions} onChange={setFrom} />
            <span>to</span>
            <Select
              style={{ width: 120 }}
              value={until}
              options={hourOptions.map((o) => ({ ...o, disabled: o.value <= from }))}
              onChange={setUntil}
            />
            <Button onClick={add} disabled={!day || until <= from}>
              Add
            </Button>
          </Space>
          {offered.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {offered.map((d) => (
                <div key={d.day} style={{ marginBottom: 4 }}>
                  <strong style={{ marginRight: 8 }}>{moment(d.day).format("ddd, MMM D")}</strong>
                  {d.time.map((h) => (
                    <Tag key={h} closable onClose={() => remove(d.day, h)}>
                      {getFormattedHours(h)}
                    </Tag>
                  ))}
                </div>
              ))}
            </div>
          )}
          <Button type="primary" onClick={send} loading={sending} disabled={!offered.length}>
            Send to candidate
          </Button>
        </div>
      }
    />
  );
};

// Stored slots are UTC; group them under the local day each one falls on.
const localGroups = (slots: IAvailableSlot[]) => {
  const groups = new Map<string, { label: string; sort: number; items: { utcDate: string; utcHour: number; hour: number }[] }>();
  slots.forEach((d) =>
    d.time.forEach((h) => {
      const l = utcSlotToLocalDisplay(d.date, h, d.year);
      const label = l.when.format("ddd, MMM D");
      const g = groups.get(label) || { label, sort: l.when.valueOf(), items: [] };
      g.items.push({ utcDate: d.date, utcHour: h, hour: l.hour });
      groups.set(label, g);
    })
  );
  return Array.from(groups.values())
    .sort((a, b) => a.sort - b.sort)
    .map((g) => ({ ...g, items: g.items.sort((a, b) => a.hour - b.hour) }));
};

const bannerTitle = (s: PendingSuggestion) => {
  const job = s.positionTitle ? ` (${s.positionTitle})` : "";
  if (s.reason === "recruiter_offer")
    return `${s.requestedByName || "Your recruiter"} is available at these times${s.positionTitle ? ` for ${s.positionTitle}` : ""}`;
  if (s.reason === "fit_manager")
    return `Please open a slot that fits the hiring manager${job}`;
  if (s.reason === "add_availability")
    return `Please add your availability so ${s.candidateName || "the candidate"} can be scheduled${job}`;
  return `Please open these slots for ${s.candidateName || "an interview"}${job}`;
};

interface PendingSuggestion {
  _id: string;
  requestedByName?: string;
  candidateName?: string;
  positionTitle?: string;
  reason: string;
  slots: IAvailableSlot[];
}

// On a manager's or candidate's own calendar: requests to open slots, each
// addable to their availability in one click.
export const PendingSuggestions: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const [items, setItems] = useState<PendingSuggestion[]>([]);
  const [busy, setBusy] = useState<string>();

  const load = () =>
    axiosInstant
      .get(endpoints.slotSuggestions.mine)
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const act = async (id: string, kind: "accept" | "dismiss", slots?: IAvailableSlot[]) => {
    setBusy(id);
    try {
      if (kind === "accept") {
        await axiosInstant.post(endpoints.slotSuggestions.accept(id), slots ? { slots } : {});
        showSuccess("Added to your availability");
      } else {
        await axiosInstant.post(endpoints.slotSuggestions.dismiss(id));
      }
      await load();
      onChanged?.();
    } catch (err) {
      showError(err);
    }
    setBusy(undefined);
  };

  if (!items.length) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      {items.map((s) => (
        <Alert
          key={s._id}
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message={bannerTitle(s)}
          description={
            <div>
              <Typography.Text type="secondary" style={{ display: "block", marginBottom: 6 }}>
                Times are in your time zone: {zoneLabel()}
              </Typography.Text>
              {localGroups(s.slots).map((g) => (
                <div key={g.label} style={{ marginBottom: 4 }}>
                  <Space wrap>
                    <strong>{g.label}</strong>
                    {g.items.map((it) => (
                      <Tag
                        key={`${it.utcDate}-${it.utcHour}`}
                        color="blue"
                        style={{ cursor: busy ? "default" : "pointer" }}
                        onClick={() => !busy && act(s._id, "accept", [{ date: it.utcDate, time: [it.utcHour] }])}
                      >
                        + {getFormattedHours(it.hour)}
                      </Tag>
                    ))}
                  </Space>
                </div>
              ))}
              {s.slots.length > 0 && (
                <Typography.Text type="secondary" style={{ display: "block", margin: "6px 0" }}>
                  Click a time to add just that one, or add them all.
                </Typography.Text>
              )}
              <Space>
                {s.slots.length > 0 ? (
                  <Button type="primary" loading={busy === s._id} onClick={() => act(s._id, "accept")}>
                    Add all to my availability
                  </Button>
                ) : (
                  <Typography.Text type="secondary">
                    Select the hours you are free in the calendar below and save.
                  </Typography.Text>
                )}
                <Button disabled={!!busy} onClick={() => act(s._id, "dismiss")}>
                  Dismiss
                </Button>
              </Space>
            </div>
          }
        />
      ))}
    </div>
  );
};
